import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';
import {
  fetchInvoices,
  exportInvoicesExcel,
  recreateInvoicePdf,
  importOpenBalance,
  getAvailableImportFiles,
  getImportJobStatus,
  type ImportJobStatusResponse,
} from '../../common/api/invoicesService';

export interface InvoiceListItem {
  referenceNumber: string;
  discriminator: string;
  number: number;
  invoiceDate: string;
  entryDate: string;
  dueDate: string;
  booking: string;
  delayFeeP: number;
  compensationInvoiceId: string;
  originalInvoiceId: string;
  siteId: string;
  isAdvance: boolean;
  id: string;
  payerName: string;
  payerPhoneNumber: string;
  payerEmail: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  bookingNumber?: number;
  totalAmount?: number;
  openBalance?: number;
}

export interface invoiceFilter extends PaginationParameters {
  searchFilter?: string;
  invoiceNumberFrom?: number;
  invoiceNumberTo?: number;
  dateFrom?: string;
  dateTo?: string;
  customerName?: string;
  bookingNumberSearch?: number;
  openBalanceOnly?: boolean;
}

export interface InvoicesState {
  invoiceItems: InvoiceListItem[];
  invoiceItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  invoicePagination: PaginationResponse;
  searchFilters: invoiceFilter;
  availableFiles: string[];
  availableFilesStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  importJobStatus: ImportJobStatusResponse | null;
  importJobStatusLoading: boolean;
}

const initialState: InvoicesState = {
  invoiceItems: [],
  invoiceItemsStatus: LOADING_STATUS.IDLE,
  invoicePagination: InitialPaginationResponse,
  searchFilters: {
    PageNumber: 1,
    PageSize: 10,
  } as invoiceFilter,
  availableFiles: [],
  availableFilesStatus: LOADING_STATUS.IDLE,
  importJobStatus: null,
  importJobStatusLoading: false,
};

export const getInvoicesAsync = createAsyncThunk(
  'invoices/getInvoices',
  async (invoicesFilter: invoiceFilter, { rejectWithValue }) => {
    try {
      const response = await fetchInvoices(invoicesFilter);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const exportInvoicesExcelAsync = createAsyncThunk(
  'invoices/exportExcel',
  async (invoicesFilter: invoiceFilter, { rejectWithValue }) => {
    try {
      return await exportInvoicesExcel(invoicesFilter);
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const recreateInvoicePdfAsync = createAsyncThunk(
  'invoices/recreatePdf',
  async (
    { invoiceId, locale }: { invoiceId: string; locale: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await recreateInvoicePdf(invoiceId, locale);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getAvailableImportFilesAsync = createAsyncThunk(
  'invoices/getAvailableImportFiles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAvailableImportFiles();
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const importOpenBalanceAsync = createAsyncThunk(
  'invoices/importOpenBalance',
  async (fileName: string | undefined, { rejectWithValue }) => {
    try {
      const response = await importOpenBalance(fileName);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const pollImportJobStatusAsync = createAsyncThunk(
  'invoices/pollImportJobStatus',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const response = await getImportJobStatus(jobId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    setSearchFilters: (state, action) => {
      state.searchFilters = action.payload;
    },
    resetImportJobStatus: state => {
      state.importJobStatus = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getInvoicesAsync.pending, state => {
        state.invoiceItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getInvoicesAsync.fulfilled, (state, action) => {
        state.invoiceItemsStatus = LOADING_STATUS.IDLE;
        state.invoiceItems = action.payload.data || [];
        state.invoicePagination = PopulatePagination(action.payload);
      })
      .addCase(getInvoicesAsync.rejected, state => {
        state.invoiceItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(exportInvoicesExcelAsync.pending, state => {
        state.invoiceItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(exportInvoicesExcelAsync.fulfilled, state => {
        state.invoiceItemsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(exportInvoicesExcelAsync.rejected, state => {
        state.invoiceItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(recreateInvoicePdfAsync.pending, state => {
        state.invoiceItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(recreateInvoicePdfAsync.fulfilled, state => {
        state.invoiceItemsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(recreateInvoicePdfAsync.rejected, state => {
        state.invoiceItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAvailableImportFilesAsync.pending, state => {
        state.availableFilesStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAvailableImportFilesAsync.fulfilled, (state, action) => {
        state.availableFilesStatus = LOADING_STATUS.IDLE;
        state.availableFiles = action.payload.result?.files || [];
      })
      .addCase(getAvailableImportFilesAsync.rejected, state => {
        state.availableFilesStatus = LOADING_STATUS.FAILED;
      })
      .addCase(importOpenBalanceAsync.pending, state => {
        state.invoiceItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(importOpenBalanceAsync.fulfilled, (state, action) => {
        state.invoiceItemsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(importOpenBalanceAsync.rejected, state => {
        state.invoiceItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(pollImportJobStatusAsync.pending, state => {
        state.importJobStatusLoading = true;
      })
      .addCase(pollImportJobStatusAsync.fulfilled, (state, action) => {
        state.importJobStatusLoading = false;
        state.importJobStatus = action.payload.result || action.payload;
      })
      .addCase(pollImportJobStatusAsync.rejected, state => {
        state.importJobStatusLoading = false;
      });
  },
});

export const { setSearchFilters, resetImportJobStatus } = invoicesSlice.actions;

export const selectInvoiceItems = (state: RootState) =>
  state.invoices.invoiceItems;
export const selectInvoicePagination = (state: RootState) =>
  state.invoices.invoicePagination;
export const selectInvoiceItemsLoadingStatus = (state: RootState) =>
  state.invoices.invoiceItemsStatus;
export const selectAvailableImportFiles = (state: RootState) =>
  state.invoices.availableFiles;
export const selectAvailableFilesLoadingStatus = (state: RootState) =>
  state.invoices.availableFilesStatus;
export const selectImportJobStatus = (state: RootState) =>
  state.invoices.importJobStatus;
export const selectImportJobStatusLoading = (state: RootState) =>
  state.invoices.importJobStatusLoading;

export default invoicesSlice.reducer;
